import { useQuery } from '@tanstack/react-query'
import { Alert, Col, Descriptions, Row, Tooltip, Typography } from 'antd'
import { workspaceQueries } from '../../../../../Actions'
import { creditPoolTotal, hasCreditPoolAnomaly } from '../../../../../types/credit'
import Receipts from './Receipts'
import RechargeCredits from './RechargeCredits'
import RetrieveCredits from './RetrieveCredits'

type Props = {
  workspaceId: number
}

export default function WorkspaceCredits({ workspaceId }: Props) {
  const { data: credits } = useQuery({
    ...workspaceQueries.detail(Number(workspaceId))._ctx.credits,
  })
  // 체크포인트가 없어도 폴백 재생값이 내려온다. basis 로 근거만 구분한다
  const { data: view } = useQuery({
    ...workspaceQueries.detail(Number(workspaceId))._ctx.creditPools,
  })

  const pools = view?.pools
  // 합계·이상치는 서버 판정을 그대로 쓴다. 같은 조건을 화면에서 다시 구현하면 조용히 어긋난다
  const poolsTotal = pools ? Number(view?.total ?? creditPoolTotal(pools)) : null
  const isAnomaly = pools ? (view?.hasAnomaly ?? hasCreditPoolAnomaly(pools)) : false
  const isFallback = view?.basis === 'FALLBACK'
  const available = Number(credits?.available ?? 0)
  // 합산 잔액은 구독이 ACTIVE 가 아니면 구독 풀을 0 으로 본다. 폴백은 재생 범위도 달라 어긋날 수 있다
  const hasTotalMismatch = poolsTotal !== null && poolsTotal !== available

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {credits?.available !== null && credits?.available !== undefined && (
        <div>
          <Typography.Title level={4}>크레딧: {credits.available}</Typography.Title>

          {pools ? (
            <>
              <Descriptions column={5} size="small" bordered>
                <Descriptions.Item label="구독">{pools.subscription}</Descriptions.Item>
                <Descriptions.Item label="어드민">{pools.adminGrant}</Descriptions.Item>
                <Descriptions.Item label="쿠폰">{pools.coupon}</Descriptions.Item>
                <Descriptions.Item label="단건">{pools.singlePayment}</Descriptions.Item>
                <Descriptions.Item label="풀 합계">{poolsTotal}</Descriptions.Item>
              </Descriptions>
              {/* 폴백은 값이 없는 게 아니라 재생 범위가 다르다. 어드민 회수 상한도 이 값을 쓴다 */}
              {isFallback ? (
                <Tooltip title="확정 체크포인트가 아직 없어 현행 윈도우를 재생한 값입니다. 재생 시작일 이전에 부여된 잔량은 잡히지 않지만, 위 합산 잔액과 어드민 회수 상한이 쓰는 값과 같은 범위입니다.">
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    현재 기간 재생 기준{view?.windowStart ? ` · ${view.windowStart} 이후` : ''}
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </>
          ) : null}

          {pools && isAnomaly ? (
            <Alert
              style={{ marginTop: 8 }}
              type="warning"
              showIcon
              message="출처별 잔량을 그대로 믿으면 안 됩니다"
              description={`출처를 못 찾은 사용분 ${pools.unappliedConsumption} · 출처 미태깅 지급분 ${pools.untaggedIssuance}. 0 이 아니면 역산이 어긋난 상태이므로 원장을 직접 확인하세요.`}
            />
          ) : null}

          {hasTotalMismatch ? (
            <Alert
              style={{ marginTop: 8 }}
              type="info"
              showIcon
              message={`풀 합계(${poolsTotal})와 합산 잔액(${credits.available})이 다릅니다`}
              description={`합산 잔액은 구독이 ACTIVE 가 아닐 때 구독 풀을 사용 불가로 보고 제외합니다(단건 풀은 비활성에서도 유지). ${isFallback ? '폴백 기준이라 재생 시작일 이전 잔량이 빠진 것도 원인일 수 있습니다. ' : ''}구독 상태를 함께 확인하세요.`}
            />
          ) : null}

          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={12}>
              <RechargeCredits workspaceId={workspaceId} />
            </Col>
            <Col span={12}>
              <RetrieveCredits workspaceId={workspaceId} />
            </Col>
          </Row>

          <Typography.Title level={4}>크레딧 변동 내역</Typography.Title>
          <Receipts workspaceId={workspaceId} />
        </div>
      )}
    </>
  )
}
